/**
 * Spec 088c — ScoreBadge
 *
 * The ONLY sanctioned way to render a score in the GEO Observatory SPA. Uses the
 * `SCORE_DESIGN` registry for all visual tokens; consumers cannot override
 * color, icon, or tooltip (and passing an unknown `scoreType` is a
 * compile-time error — see Gate 088c.B).
 *
 * Wrapped in `forwardRef` so it works inside tooltips / popovers / hover
 * cards that need to forward a DOM ref to the trigger element.
 *
 * Spec 087 gap 087-4: optional `previousValue` renders a small delta pill
 * next to the value when the delta is a non-zero integer. Callers without
 * history omit the prop entirely.
 */
import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { Icon } from './ui/Icon.js';
import type { ScoreType } from '../lib/score-design.js';
import { SCORE_DESIGN } from '../lib/score-design.js';

export interface ScoreBadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /**
   * Required discriminated-union prop. Any string outside the closed
   * `ScoreType` union is a TypeScript compile error.
   */
  scoreType: ScoreType;
  /** Score value; `null` renders as an em-dash. */
  value: number | null;
  /** Optional size override; defaults to 'md'. */
  size?: 'sm' | 'md' | 'lg';
  /** Show the label next to the value. Defaults to false (icon + value only). */
  showLabel?: boolean;
  /**
   * Optional prior value used to render a trend delta pill. When present
   * alongside `value` and the rounded delta is non-zero, a small
   * up/down arrow pill is rendered next to the value.
   */
  previousValue?: number | null;
}

/** Spec 230 — `.hub-chip` sets the pill's own padding/font; size only scales
 * the type on top of that shared shape. */
const SIZE_CLASSES: Record<NonNullable<ScoreBadgeProps['size']>, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

/** Boxicons size with font-size, so the icon rides the same type scale as
 * the badge text (text-xs=12px, text-sm=14px, text-base=16px — the exact
 * pixel values the old lucide `size` prop used). */
const ICON_SIZE_CLASSES: Record<NonNullable<ScoreBadgeProps['size']>, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

function formatValue(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  // Assume the value is a 0–100 scale; format as integer. Downstream consumers
  // should pre-scale if their source metric is 0–1 or 0–10.
  return `${Math.round(value)}`;
}

/** Integer-rounded delta; returns 0 when either operand is null / NaN. */
function computeDelta(
  value: number | null | undefined,
  previousValue: number | null | undefined,
): number {
  if (
    value === null ||
    value === undefined ||
    previousValue === null ||
    previousValue === undefined ||
    Number.isNaN(value) ||
    Number.isNaN(previousValue)
  ) {
    return 0;
  }
  return Math.round(value) - Math.round(previousValue);
}

/**
 * ScoreBadge — renders a pill with the design-system-registered color +
 * icon + value for the given `scoreType`.
 *
 * Usage:
 *   <ScoreBadge scoreType="VisibilityScore" value={72} />
 *   <ScoreBadge scoreType="CoverageScore" value={null} size="sm" />
 *   <ScoreBadge scoreType="CoverageScore" value={72} previousValue={65} />
 *
 * Compile error:
 *   <ScoreBadge scoreType="NotAScore" value={50} />  // ❌ tsc fails
 */
export const ScoreBadge = forwardRef<HTMLSpanElement, ScoreBadgeProps>(
  function ScoreBadge(
    {
      scoreType,
      value,
      size = 'md',
      showLabel = false,
      previousValue,
      className = '',
      ...rest
    },
    ref,
  ) {
    const design = SCORE_DESIGN[scoreType];
    const sizeClass = SIZE_CLASSES[size];
    const delta = computeDelta(value, previousValue);

    return (
      <span
        ref={ref}
        className={`hub-chip hub-chip--${design.tone} ${sizeClass} ${className}`}
        title={design.tooltip}
        data-score-type={scoreType}
        {...rest}
      >
        <Icon name={design.icon} className={ICON_SIZE_CLASSES[size]} />
        {showLabel && <span>{design.label}</span>}
        <span className="tabular-nums">{formatValue(value)}</span>
        {delta !== 0 && (
          <span
            data-testid={`score-trend-${scoreType}`}
            className={`hub-chip ${delta > 0 ? 'hub-chip--ok' : 'hub-chip--fail'} ml-1 text-xs`}
            title={delta > 0 ? 'Improved vs. last analysis' : 'Declined vs. last analysis'}
          >
            {delta > 0 ? (
              <Icon name="bx-trending-up" className="text-xs" />
            ) : (
              <Icon name="bx-trending-down" className="text-xs" />
            )}
            <span className="tabular-nums">
              {delta > 0 ? `+${delta}` : `${delta}`}
            </span>
          </span>
        )}
      </span>
    );
  },
);
